import React, {useEffect, useState} from "react";
import Button from "@material-ui/core/Button";
import {getOrganizationUnit, getSubscriptionsCount} from "../../api/organization-unit-functions";
import {useOrganization} from "../organizations/organization-context";
import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import {SubscriptionTransfer} from "./SubscriptionTransfer";
import {useTranslation} from "react-i18next";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import {useNavigate} from "react-router-dom";
import * as _ from 'lodash';
import {transferSubscriptions} from "../../api/subscriptions-functions";
import {rand, run} from "../../utils/function-util";
import {FormStatus} from "./form-status";
import {ErrorMessage} from "./ErrorMessage";
import {KeyValue} from "../../models/shared";
import {SubscriptionCount, SubscriptionType} from "../../models/subscription-model";

const useStyles = makeStyles(theme => ({
    dialogContent: {},
    container: {},
    formsContainer: {
        marginTop: theme.spacing(2)
    },
    addBtn: {
        marginTop: theme.spacing(4)
    },
    actions: {
        '& > *': {
            marginLeft: theme.spacing(2)
        }
    }
}));
export const DistributeSubscriptions = ({firstSourceOrgUnitId}: any) => {
    const [transfers, setTransfers] = useState<any[]>([]);
    const [currentOrganization] = useOrganization();
    const classes = useStyles();
    const {t} = useTranslation();
    const navigate = useNavigate();

    useEffect(run(async () => {
        const orgUnit = await getOrganizationUnit(currentOrganization!.id, firstSourceOrgUnitId);
        const counts = await getSubscriptionsCount(currentOrganization!.id, firstSourceOrgUnitId);
        return {orgUnit, counts};
    }, ({orgUnit, counts}: any) => {
        setTransfers([{
            id: rand().toString(),
            data: {
                source: {id: orgUnit.id, name: orgUnit.name},
                destination: {id: null, name: ''},
                available: {
                    pro: counts.find((ac: any) => ac.subscriptionType === SubscriptionType.Provider15).availableSubscriptions,
                    standard: counts.find((ac: any) => ac.subscriptionType === SubscriptionType.Standard).availableSubscriptions
                },
                selected: {
                    pro: 0,
                    standard: 0
                },
            },
        }]);
    }), []);

    const emptyTransferRow = {
        id: rand().toString(),
        data: {
            source: {id: null, name: ''},
            destination: {id: null, name: ''},
            available: {
                pro: 0,
                standard: 0
            },
            selected: {
                pro: 0,
                standard: 0
            }
        },
    };
    const addTransfer = () => {
        setTransfers([...transfers, emptyTransferRow]);
    };
    const handleCancelAction = () => {
        navigate(`/organization-units/${firstSourceOrgUnitId}`);
    };
    const submitTransfer = async (transfer: any) => {
        if (transfer.status === FormStatus.successful) {
            throw new Error('Cannot resubmit successful transfer');
        }
        const id = transfer.id;
        const from = transfer.data.source.id;
        const to = transfer.data.destination.id;

        const subscriptions: any = {};
        const counts = transfer.data.selected;
        if (counts.pro) {
            subscriptions[SubscriptionType.Provider15] = counts.pro;
        }
        if (counts.standard) {
            subscriptions[SubscriptionType.Standard] = counts.standard;
        }

        updateTransfer(id, {
            status: FormStatus.processing,
            errors: null,
            correctiveAction: null
        });

        try {
            await transferSubscriptions(currentOrganization!.id, from, to, subscriptions);
            updateTransfer(id, {
                status: FormStatus.successful,
            });
        } catch (e: any) {
            const errorType = e.response && e.response.data && e.response.data.error && e.response.data.error.type ?
                e.response.data.error.type
                : undefined;
            updateTransfer(id, {
                correctiveAction: <ErrorMessage errorType={errorType}/>,
                status: FormStatus.none
            });
        }
    };
    const updateTransfer = (id: any, patch: any) => {
        setTransfers(newTransfers => {
            const index = newTransfers.findIndex(f => f.id === id);
            if (index <= -1)
                return newTransfers;
            const result = [...newTransfers];
            result[index] = _.merge({}, newTransfers[index], patch);
            return result;
        });
    };
    const validateField = (field: any, value: any) => {
        const errors: KeyValue<string> = {};
        switch (field) {
            case 'source':
                errors[field] = !value || !value.id ? 'sourceIsInvalid' : null;
                break;
            case 'destination':
                errors[field] = !value || !value.id ? 'destinationIsInvalid' : null;
                break;
            case 'selected':
                const noSubscriptionsToTransfer = Object.keys(value).every(k => value[k] < 1);
                errors[field] = noSubscriptionsToTransfer ? 'countIsInvalid' : null;
                break;
            default:
                errors[field] = !value ? 'FieldIsRequired' : null;
                break;
        }
        return errors;
    };
    const handleApply = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const validatedTransfers = transfers.map(transfer => {
            const errors = Object.keys(transfer.data).reduce((acc, name) => ({...acc, ...validateField(name, transfer.data[name])}), {});
            return {
                ...transfer,
                errors,
                correctiveAction: null
            };
        });
        if (transfersHaveErrors(validatedTransfers)) {
            return setTransfers(validatedTransfers);
        }
        for (let transfer of transfers.filter(tr => tr.status !== FormStatus.successful)) {
            await submitTransfer(transfer);
        }
    };
    const transfersHaveErrors = (transfersToValidate: any) => {
        return transfersToValidate.some((f: any) => _.values(f.errors).some((v: any) => !!v));
    };
    const handleTransferChanged = (licenseType: any, count: any, transfer: any) => {
        const update = {
            data: {
                selected: {
                    [licenseType]: count
                }
            },
            errors: {
                selected: null
            }
        };
        updateTransfer(transfer.id, update);
    };
    const handleTransferDeleted = (transfer: any) => {
        setTransfers(transfersToDelete => {
            const index = transfersToDelete.findIndex(f => f.id === transfer.id);
            if (index <= -1)
                return transfersToDelete;
            const newForms = [...transfersToDelete];
            newForms.splice(index, 1);
            return newForms;
        });
    };
    const updateTransferSourceOrDestination = (event: any) => {
        (async () => {
            const {id, newOrgUnit, isSource} = event;
            const transfer = transfers.find(tr => tr.id === id);
            if (isSource) {
                if (transfer.data.source.id !== newOrgUnit.id) {
                    const counts = await getSubscriptionsCount(currentOrganization!.id, newOrgUnit.id);

                    setTransfers(transfers.map((tr) => {
                        if (tr.id !== id) {
                            return tr;
                        }
                        const updatedTransfer = {...tr};
                        updatedTransfer.data.source = {...newOrgUnit};
                        updatedTransfer.errors = {...updatedTransfer.errors, source: null};
                        updatedTransfer.data.available = {};
                        updatedTransfer.data.available.pro
                            = counts.find((ac: SubscriptionCount) => ac.subscriptionType === SubscriptionType.Provider15)?.availableSubscriptions;
                        updatedTransfer.data.available.standard
                            = counts.find((ac: SubscriptionCount) => ac.subscriptionType === SubscriptionType.Standard)?.availableSubscriptions;
                        return updatedTransfer;
                    }));
                }
            } else {
                if (transfer.data.destination.id !== newOrgUnit.id) {
                    await getSubscriptionsCount(currentOrganization!.id, newOrgUnit.id);
                    setTransfers(transfers.map((tr) => {
                        if (tr.id !== id) {
                            return tr;
                        }
                        const updatedTransfer = {...tr};
                        updatedTransfer.data.destination = {...newOrgUnit};
                        updatedTransfer.errors = {...updatedTransfer.errors, destination: null};
                        return updatedTransfer;
                    }));
                }
            }
        })();
    };
    const displayGoBack = transfers && transfers.length && transfers.every(transfer => transfer.status === FormStatus.successful);
    return (<React.Fragment>
        <Grid container justify="center">
            {!transfers.length ? null :
                transfers.map((transfer) => <SubscriptionTransfer transfer={transfer}
                                                                  handleTransferChanged={handleTransferChanged}
                                                                  handleTransferDeleted={handleTransferDeleted}
                                                                  handleOrgUnitChanged={updateTransferSourceOrDestination}
                                                                  isOnlyRow={transfers.length === 1}
                                                                  organizationId={currentOrganization!.id}
                                                                  key={`subscriptions-transfers-${transfer.id}`}>
                    {transfer.correctiveAction}
                </SubscriptionTransfer>)}
        </Grid>
        <Grid container justify="flex-start">
            <IconButton aria-label="add" className={classes.addBtn} onClick={addTransfer}>
                <AddIcon/>
            </IconButton>
        </Grid>
        <Grid container justify="flex-end" className={classes.actions}>
            {displayGoBack ?
                <Button variant="contained" color="primary" size="medium" onClick={handleCancelAction}>
                    {t('_Common.Labels.OK')}
                </Button> :
                <React.Fragment>
                    <Button variant="outlined" color="primary" size="medium" onClick={handleCancelAction}>
                        {t('_Common.Labels.Cancel')}
                    </Button>
                    <Button variant="contained" color="primary" size="medium"
                            disabled={transfersHaveErrors(transfers)} onClick={handleApply}>
                        {t('_Common.Labels.Apply')}
                    </Button>
                </React.Fragment>}
        </Grid>

    </React.Fragment>);
};
